import { useState, FormEvent, useMemo } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { v4 as uuidv4 } from "uuid";
import { PolicyDecisionActionTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import { ButtonTypes } from "@enums/button-types";
import { PolicyDecisionOperationTypes } from "@enums/policy-decision-operation-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { PolicyDispositionTypes } from "@enums/policy-disposition-types";
import { AxnAttributeType, AxnAttributeTypeField, AxnCompany } from "@idw-types/data/reference-data/axn";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import {
    FormattedRpServiceConfigurationAttribute,
    FormattedRpServiceConfigurationPolicyConfigDecision,
    FormattedRpServiceConfigurationServiceOffering
} from "@idw-types/data/rp-service-configuration";
import { DefaultModal } from "@containers/common/modals/default-modal/default-modal";
import { RpServiceConfigurationPolicyDecisionContainer } from "@containers/common/rp-service-configuration-policy-decision-container/rp-service-configuration-policy-decision-container";
import { AvailableAssertionStatus } from "@idw-types/data/reference-data/generic";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";
import { PolicyDetailErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";

type AddRpServiceConfigurationPolicyConfigDecisionModalProps = {
    onModalClose: () => void;
    attributes: Array<FormattedRpServiceConfigurationAttribute>;
    isUsingThemedUI: boolean;
    axnuiContent: AXNUIContent;
    acquiredAttributes: Array<FormattedRpServiceConfigurationAttribute>;
    unformattedAcquiredAttributes: Array<AxnAcquiredAttribute>;
    axnRpServices: Array<AxnRpService>;
    axnCompanies: Array<AxnCompany>;
    serviceOrganizationID: string;
    validationErrors: PolicyDetailErrors;
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    policyRetries: string;
    simpleRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    rpServiceID: string;
    workflowID: string;
    addRpServiceConfigurationPolicyConfigDecision: (decision: FormattedRpServiceConfigurationPolicyConfigDecision, AXNUIContent: AXNUIContent) => void;
};

type AddRpServiceConfigurationPolicyConfigDecisionModalState = {
    decision: FormattedRpServiceConfigurationPolicyConfigDecision;
};

export const AddRpServiceConfigurationPolicyConfigDecisionModal = (props: AddRpServiceConfigurationPolicyConfigDecisionModalProps) => {
    const {
        attributes,
        isUsingThemedUI,
        acquiredAttributes,
        axnCompanies,
        serviceOrganizationID,
        axnuiContent,
        axnRpServices,
        axnAttributeTypes,
        axnAttributeTypeFields,
        unformattedAcquiredAttributes,
        validationErrors,
        serviceOfferings,
        policyRetries,
        axnAvailableAssertionStatuses,
        onModalClose,
        addRpServiceConfigurationPolicyConfigDecision,
        simpleRPServiceWorkflows,
        rpServiceID,
        workflowID
    } = props;

    const [newAXNUIContent, setAXNUIContent] = useState<AXNUIContent>(
        axnuiContent);

    const handleSetRetryMessage = (key: string, value: string) => {
        const defaultContentPolicyRetryMessage = {
            retryMessage: value
        };

        const newPropertyValue: AXNUIContent = {
            ...newAXNUIContent,
            policyDecisionRetryMessage: {
                ...newAXNUIContent.policyDecisionRetryMessage,
                [key]: {
                    ...defaultContentPolicyRetryMessage
                }
            }
        };
        setAXNUIContent(newPropertyValue);

    };

    const [state, setState] = useState<AddRpServiceConfigurationPolicyConfigDecisionModalState>({
        decision: {
            conditions: [],
            description: null,
            disposition: PolicyDispositionTypes.POSITIVE,
            id: null,
            obligationValue: "",
            policyDecisionOperationType: PolicyDecisionOperationTypes.AND,
            policyDecisionType: PolicyDecisionActionTypes.APPROVE,
            retries: "0",
            status: EntityStatusTypes.ACTIVE,
            uuid: uuidv4()
        }
    });

    const setDecisionState = (decision: FormattedRpServiceConfigurationPolicyConfigDecision) => {
        setState({
            ...state,
            decision: decision
        });
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        addRpServiceConfigurationPolicyConfigDecision(state.decision, newAXNUIContent);
        onModalClose();
    };

    const isSubmitDisabled = useMemo(() => {
        if (state.decision.policyDecisionType === PolicyDecisionActionTypes.OBLIGATION && !state.decision.obligationValue) {
            return true;
        }
        if (state.decision.conditions.length === 0) {
            return true;
        }
        //todo - add more validation here

        return false;
    }, [state.decision.obligationValue, state.decision.policyDecisionType, state.decision.conditions]);

    return (
        <DefaultModal
            automationId="add-rp-service-configuration-policy-config-decision-modal"
            ariaLabelledBy="add-rp-service-configuration-policy-config-decision-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={true}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-rp-service-configuration-policy-config-decision-title"} closeButton>
                    <Modal.Title>{resources.modals.addRpServiceConfigurationPolicyConfigDecision.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <RpServiceConfigurationPolicyDecisionContainer
                        isDisabled={false}
                        isUsingThemedUI={isUsingThemedUI}
                        axnuiContent={newAXNUIContent}
                        axnCompanies={axnCompanies}
                        serviceOrganizationID={serviceOrganizationID}
                        axnRpServices={axnRpServices}
                        decision={state.decision}
                        validationErrors={validationErrors}
                        attributes={attributes}
                        acquiredAttributes={acquiredAttributes}
                        policyRetries={policyRetries}
                        serviceOfferings={serviceOfferings}
                        axnAttributeTypes={axnAttributeTypes}
                        axnAttributeTypeFields={axnAttributeTypeFields}
                        simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                        unformattedAcquiredAttributes={unformattedAcquiredAttributes}
                        axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                        rpServiceID={rpServiceID}
                        workflowID={workflowID}
                        updateAXNUIContentPolicyDecisionRetryMessage={handleSetRetryMessage}
                        setDecision={setDecisionState}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-policy-decision-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <Button
                        id="add-policy-decision-submit-button"
                        variant="primary"
                        disabled={isSubmitDisabled}
                        type={ButtonTypes.SUBMIT}
                    >
                        {resources.modals.addRpServiceConfigurationPolicyConfigDecision.buttons.addRule.label}
                    </Button>
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};